import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(isBetween);
dayjs.extend(quarterOfYear);
dayjs.extend(customParseFormat);

/**
 * Génère les 4 périodes comparatives selon l'échelonnement choisi
 * @param {string} echelonnement - 'date' | 'mois' | 'trimestre' | 'semestre'
 * @param {string|Date|dayjs.Dayjs} startDate - Date de début
 * @param {string|Date|dayjs.Dayjs} endDate - Date de fin
 * @returns {Array<{ index: number, label: string, start: dayjs.Dayjs, end: dayjs.Dayjs, isPastOrCurrent: boolean }>}
 */
export const generateComparativePeriods = (echelonnement, startDate, endDate) => {
    const today = dayjs().endOf('day');
    const start = startDate ? dayjs(startDate) : dayjs().startOf('year');
    const end = endDate ? dayjs(endDate) : dayjs().endOf('year');
    const refYear = start.isValid() ? start.year() : dayjs().year();

    const periods = [];
    const normalizedEchelon = (echelonnement || '').toLowerCase().trim();

    if (normalizedEchelon === 'mois') {
        // 4 mois consécutifs à partir du mois de début
        const baseMonth = start.isValid() ? start.startOf('month') : dayjs().startOf('year');
        for (let i = 0; i < 4; i++) {
            const pStart = baseMonth.add(i, 'month').startOf('month');
            const pEnd = pStart.endOf('month');
            const isPastOrCurrent = pStart.isBefore(today) || pStart.isSame(today, 'day');
            periods.push({
                index: i + 1,
                label: `Ventes\ndu ${pStart.format('DD/MM/YYYY')}\nau ${pEnd.format('DD/MM/YYYY')}`,
                shortLabel: pStart.format('MMM YYYY'),
                start: pStart,
                end: pEnd,
                isPastOrCurrent
            });
        }
    } else if (normalizedEchelon === 'trimestre') {
        // 4 trimestres de l'année de référence (T1, T2, T3, T4)
        for (let q = 1; q <= 4; q++) {
            const startMonth = (q - 1) * 3;
            const pStart = dayjs().year(refYear).month(startMonth).startOf('month');
            const pEnd = pStart.add(2, 'month').endOf('month');
            const isPastOrCurrent = pStart.isBefore(today) || pStart.isSame(today, 'day');
            periods.push({
                index: q,
                label: `Ventes\ndu ${pStart.format('DD/MM/YYYY')}\nau ${pEnd.format('DD/MM/YYYY')}`,
                shortLabel: `T${q} ${refYear}`,
                start: pStart,
                end: pEnd,
                isPastOrCurrent
            });
        }
    } else if (normalizedEchelon === 'semestre') {
        // 4 semestres (S1 N-1, S2 N-1, S1 N, S2 N)
        const semestres = [
            { year: refYear, sem: 1, startM: 0, endM: 5 },
            { year: refYear, sem: 2, startM: 6, endM: 11 },
            { year: refYear + 1, sem: 1, startM: 0, endM: 5 },
            { year: refYear + 1, sem: 2, startM: 6, endM: 11 }
        ];
        semestres.forEach((s, idx) => {
            const pStart = dayjs().year(s.year).month(s.startM).startOf('month');
            const pEnd = dayjs().year(s.year).month(s.endM).endOf('month');
            const isPastOrCurrent = pStart.isBefore(today) || pStart.isSame(today, 'day');
            periods.push({
                index: idx + 1,
                label: `Ventes\ndu ${pStart.format('DD/MM/YYYY')}\nau ${pEnd.format('DD/MM/YYYY')}`,
                shortLabel: `S${s.sem} ${s.year}`,
                start: pStart,
                end: pEnd,
                isPastOrCurrent
            });
        });
    } else {
        // Mode 'date' : 4 intervalles de durée égale à la période saisie
        const durationDays = end.isValid() && start.isValid() && end.diff(start, 'day') >= 0 
            ? Math.max(1, end.diff(start, 'day') + 1)
            : 15;
            
        for (let i = 0; i < 4; i++) {
            const pStart = start.add(i * durationDays, 'day');
            const pEnd = pStart.add(durationDays - 1, 'day');
            const isPastOrCurrent = pStart.isBefore(today) || pStart.isSame(today, 'day');
            periods.push({
                index: i + 1,
                label: `Ventes\ndu ${pStart.format('DD/MM/YYYY')}\nau ${pEnd.format('DD/MM/YYYY')}`,
                shortLabel: `${pStart.format('DD/MM')} - ${pEnd.format('DD/MM')}`,
                start: pStart,
                end: pEnd,
                isPastOrCurrent
            });
        }
    }

    return periods;
};

/**
 * Parse une date sous n'importe quel format (ISO, DD/MM/YYYY, Timestamp, etc.)
 */
export const parseAnyDate = (dateVal) => {
    if (!dateVal) return null;
    if (dayjs.isDayjs(dateVal)) return dateVal;
    if (dateVal instanceof Date) return dayjs(dateVal);
    
    const str = String(dateVal).trim();
    if (/^\d{2}[/-]\d{2}[/-]\d{4}/.test(str)) {
        const parts = str.split(/[/-]/);
        return dayjs(`${parts[2]}-${parts[1]}-${parts[0]}`);
    }
    const d = dayjs(str);
    return d.isValid() ? d : null;
};

/**
 * Vérifie si un élément date est dans une période
 */
const isInPeriod = (itemDate, periodStart, periodEnd) => {
    const d = parseAnyDate(itemDate);
    if (!d || !d.isValid()) return false;
    return (d.isAfter(periodStart, 'day') || d.isSame(periodStart, 'day')) &&
           (d.isBefore(periodEnd, 'day') || d.isSame(periodEnd, 'day'));
};

/**
 * Extrait le CA réel d'un dossier / circuit
 */
export const getCircuitCA = (dossier) => {
    if (!dossier) return 0;
    
    // 1. Direct montant
    if (dossier.montantTotal && Number(dossier.montantTotal) > 0) return Number(dossier.montantTotal);
    if (dossier.montant && Number(dossier.montant) > 0) return Number(dossier.montant);
    if (dossier.total && Number(dossier.total) > 0) return Number(dossier.total);

    // 2. Factures du dossier (factureCircuits ou factures)
    const factures = dossier.factureCircuits || dossier.factures || [];
    if (Array.isArray(factures) && factures.length > 0) {
        const factSum = factures.reduce((sum, f) => {
            if (f.type === 'AV' || f.type === 'FP' || f.typeFacture === 'AV') return sum;
            if (Array.isArray(f.frais) && f.frais.length > 0) {
                const globalPax = Number(dossier.pax || 1);
                const lSum = f.frais.reduce((fTotal, item) => {
                    if (item.type === 'A' || item.nature === 'A' || item.isCanceled) return fTotal;
                    const pu = Number(item.montant || item.prixUnitaire || 0);
                    const qte = Number(item.quantite || 1);
                    const itemPax = Number(item.nbrPax || item.pax || globalPax);
                    const mult = item.currency === 'EURO' ? 655.957 : 1;
                    return fTotal + (pu * qte * itemPax * mult);
                }, 0);
                return sum + lSum;
            }
            return sum + Number(f.montant || f.total || f.totalFacture || 0);
        }, 0);
        if (factSum > 0) return factSum;
    }

    // 3. Somme des paiements du dossier
    if (Array.isArray(dossier.paiements) && dossier.paiements.length > 0) {
        const pSum = dossier.paiements.reduce((sum, p) => sum + Number(p.montant || 0), 0);
        if (pSum > 0) return pSum;
    }

    // 4. Somme des prestations (Hebergements + Excursions + Transferts + Locations)
    let prestSum = 0;
    const pax = Number(dossier.pax || 1);
    (dossier.hotels || []).forEach(h => {
        const pu = Number(h.prixUnitaire || h.tarif || 0);
        const nuitee = Number(h.nuitee || h.nombreNuitee || 1);
        const hPax = Number(h.pax || pax);
        prestSum += (pu * nuitee * hPax) || Number(h.montant || 0);
    });
    (dossier.excursions || []).forEach(e => {
        const pu = Number(e.prixUnitaire || e.tarif || 0);
        const ePax = Number(e.pax || pax);
        prestSum += (pu * ePax) || Number(e.montant || 0);
    });
    (dossier.transferts || []).forEach(t => {
        const pu = Number(t.prixUnitaire || t.tarif || 0);
        const tPax = Number(t.pax || pax);
        prestSum += (pu * tPax) || Number(t.montant || 0);
    });
    (dossier.locations || []).forEach(l => {
        const pu = Number(l.prixUnitaire || l.tarif || 0);
        const nbJours = Number(l.nombreJour || l.jours || 1);
        prestSum += (pu * nbJours) || Number(l.montant || 0);
    });
    if (prestSum > 0) return prestSum;

    // 5. Si pas de CA explicite mais débours présents, CA minimum = Débours
    const debours = getCircuitDebours(dossier);
    if (debours > 0) {
        return Math.round(debours * 1.25); // Estimation 20% de marge brute
    }

    return 0;
};

/**
 * Extrait les Débours réels d'un dossier / circuit
 */
export const getCircuitDebours = (dossier) => {
    if (!dossier) return 0;
    let debours = 0;

    // Frais annexes
    if (Array.isArray(dossier.frais)) {
        debours += dossier.frais.reduce((sum, f) => sum + Number(f.montant || 0), 0);
    }

    // Enveloppes (dépouillement réel ou montant estimé)
    if (Array.isArray(dossier.envelops)) {
        debours += dossier.envelops.reduce((sum, e) => {
            const depouillement = Number(e.depouillement || e.montantReel || 0);
            const estime = Number(e.montant || e.montantEstime || 0);
            return sum + (depouillement > 0 ? depouillement : estime);
        }, 0);
    }

    return debours;
};

/**
 * Extrait les statistiques financières d'un billet d'avion
 */
export const getTicketStats = (ticket) => {
    const tarif = Number(ticket.tarif || ticket.montant_vente || ticket.montant || ticket.total || 0);
    const frais = Number(ticket.frais || ticket.fraisService || ticket.frais_service || 0);
    const ca = tarif > 0 ? (tarif + (ticket.total ? 0 : frais)) : Number(ticket.total || 0);
    
    let achat = Number(ticket.montantAchat || ticket.montant_achat || ticket.prixAchat || ticket.debours || 0);
    let marge = Number(ticket.marge !== undefined && ticket.marge !== null ? ticket.marge : 0);

    if (achat === 0 && marge > 0) {
        achat = Math.max(0, ca - marge);
    } else if (achat === 0 && marge === 0) {
        if (frais > 0) {
            marge = frais;
            achat = Math.max(0, ca - frais);
        } else {
            // Marge billetterie réelle / commission standard (7%)
            marge = Math.round(ca * 0.07);
            achat = ca - marge;
        }
    } else {
        marge = ca - achat;
    }

    return { ca, achat, marge };
};

/**
 * Calcule tous les agrégats financiers pour les 5 documents et graphiques
 * @param {Object} rawData - { factures: [], planeTickets: [], circuits: [], compagnies: [], toClients: [] }
 * @param {Array} periods - Les 4 périodes générées par generateComparativePeriods
 * @param {string} activityFilter - 'tous' | 'billetterie' | 'tourisme'
 */
export const calculateDashboardAnalytics = (rawData = {}, periods = [], activityFilter = 'tous') => {
    const factures = rawData.factures || [];
    const planeTickets = rawData.planeTickets || [];
    const circuits = rawData.circuits || [];
    const filter = (activityFilter || 'tous').toLowerCase();

    // 1. Calculs des agrégats pour chaque période
    const periodData = periods.map(p => {
        if (!p.isPastOrCurrent) {
            // Période future : vide
            return {
                period: p,
                isEmpty: true,
                billetterie: { ca: 0, achats: 0, marge: 0, pctMarge: 0 },
                tourisme: { ca: 0, debours: 0, marge: 0, pctMarge: 0 },
                global: { ca: 0, debours: 0, marge: 0, pctMarge: 0 },
                prestations: {
                    circuits: { ca: 0, debours: 0, marge: 0, pctMarge: 0 },
                    excursions: { ca: 0, debours: 0, marge: 0, pctMarge: 0 },
                    transferts: { ca: 0, debours: 0, marge: 0, pctMarge: 0 },
                    sejours: { ca: 0, debours: 0, marge: 0, pctMarge: 0 }
                },
                compagnies: {},
                clients: {}
            };
        }

        // --- BILLETTERIE ---
        let caBilletterie = 0;
        let achatsBilletterie = 0;
        let margeBilletterie = 0;
        const compagniesMap = {};

        // A. Depuis les PlaneTickets émis dans la période
        planeTickets.forEach(ticket => {
            const ticketDate = ticket.date || ticket.dateEmission || ticket.dateVol || ticket.createdAt;
            if (isInPeriod(ticketDate, p.start, p.end)) {
                const stats = getTicketStats(ticket);
                caBilletterie += stats.ca;
                achatsBilletterie += stats.achat;
                margeBilletterie += stats.marge;

                const compName = ticket.compagnieVol?.nom || ticket.compagnie || ticket.compagnieVol || 'AUTRES COMPAGNIES';
                compagniesMap[compName] = (compagniesMap[compName] || 0) + stats.ca;
            }
        });

        // B. Depuis les Factures de type Billetterie si planeTickets est vide
        if (planeTickets.length === 0) {
            factures.forEach(fact => {
                const isBillet = fact.typeFacture === 'FB' || fact.nature === 'billetterie' || (fact.tickets && fact.tickets.length > 0);
                const factDate = fact.date || fact.createdAt;
                if (isBillet && isInPeriod(factDate, p.start, p.end)) {
                    const montantFact = Number(fact.montant || fact.total || fact.totalFacture || 0);
                    const fraisEstimated = Number(fact.fraisService || (montantFact * 0.07));
                    const achatFact = Math.max(0, montantFact - fraisEstimated);
                    
                    caBilletterie += montantFact;
                    achatsBilletterie += achatFact;
                    margeBilletterie += (montantFact - achatFact);

                    const compName = fact.compagnieVol?.nom || fact.compagnie || 'AUTRES COMPAGNIES';
                    compagniesMap[compName] = (compagniesMap[compName] || 0) + montantFact;
                }
            });
        }

        const pctMargeBilletterie = caBilletterie > 0 ? (margeBilletterie / caBilletterie) * 100 : 0;

        // --- TOURISME ---
        let caTourisme = 0;
        let deboursTourisme = 0;
        const prestationsMap = {
            circuits: { ca: 0, debours: 0, marge: 0, pctMarge: 0 },
            excursions: { ca: 0, debours: 0, marge: 0, pctMarge: 0 },
            transferts: { ca: 0, debours: 0, marge: 0, pctMarge: 0 },
            sejours: { ca: 0, debours: 0, marge: 0, pctMarge: 0 }
        };
        const clientsMap = {};

        // A. Depuis les Dossiers / Circuits
        circuits.forEach(dossier => {
            const dossierDate = dossier.date || dossier.dateDebut || dossier.dateVolArrivee || dossier.createdAt;
            if (isInPeriod(dossierDate, p.start, p.end)) {
                const dossierCA = getCircuitCA(dossier);
                const dossierDebours = getCircuitDebours(dossier);

                caTourisme += dossierCA;
                deboursTourisme += dossierDebours;

                // Ventilation par type de prestation
                const dType = (dossier.type || 'circuit').toLowerCase();
                let targetKey = 'circuits';
                if (dType.includes('excursion')) targetKey = 'excursions';
                else if (dType.includes('transfert')) targetKey = 'transferts';
                else if (dType.includes('sejour') || dType.includes('séjour')) targetKey = 'sejours';

                prestationsMap[targetKey].ca += dossierCA;
                prestationsMap[targetKey].debours += dossierDebours;

                // Ventilation par Client / Partenaire
                const clientName = dossier.toClient?.libelle || (typeof dossier.toClient === 'string' ? dossier.toClient : null) || dossier.nomClient || dossier.client?.nom || 'CLIENT COMPTANT';
                clientsMap[clientName] = (clientsMap[clientName] || 0) + dossierCA;
            }
        });

        // B. Depuis les Factures Tourisme (si circuits est vide ou factures indépendantes)
        if (circuits.length === 0) {
            factures.forEach(fact => {
                const isTourisme = fact.typeFacture === 'FC' || fact.nature === 'tourisme' || fact.dossier;
                const factDate = fact.date || fact.createdAt;
                if (isTourisme && isInPeriod(factDate, p.start, p.end)) {
                    const montantFact = Number(fact.montant || fact.total || fact.totalFacture || 0);
                    const deboursEstimated = Math.round(montantFact * 0.75);
                    caTourisme += montantFact;
                    deboursTourisme += deboursEstimated;
                    prestationsMap.circuits.ca += montantFact;
                    prestationsMap.circuits.debours += deboursEstimated;

                    const clientName = fact.toClient?.libelle || fact.nomClient || 'CLIENT COMPTANT';
                    clientsMap[clientName] = (clientsMap[clientName] || 0) + montantFact;
                }
            });
        }

        // Calcul des marges par prestation
        Object.keys(prestationsMap).forEach(k => {
            const obj = prestationsMap[k];
            obj.marge = obj.ca - obj.debours;
            obj.pctMarge = obj.ca > 0 ? (obj.marge / obj.ca) * 100 : 0;
        });

        const margeTourisme = caTourisme - deboursTourisme;
        const pctMargeTourisme = caTourisme > 0 ? (margeTourisme / caTourisme) * 100 : 0;

        // --- GLOBAL ---
        const caGlobal = caBilletterie + caTourisme;
        const deboursGlobal = achatsBilletterie + deboursTourisme;
        const margeGlobale = margeBilletterie + margeTourisme;
        const pctMargeGlobale = caGlobal > 0 ? (margeGlobale / caGlobal) * 100 : 0;

        return {
            period: p,
            isEmpty: false,
            billetterie: {
                ca: caBilletterie,
                achats: achatsBilletterie,
                marge: margeBilletterie,
                pctMarge: pctMargeBilletterie
            },
            tourisme: {
                ca: caTourisme,
                debours: deboursTourisme,
                marge: margeTourisme,
                pctMarge: pctMargeTourisme
            },
            global: {
                ca: caGlobal,
                debours: deboursGlobal,
                marge: margeGlobale,
                pctMarge: pctMargeGlobale
            },
            prestations: prestationsMap,
            compagnies: compagniesMap,
            clients: clientsMap
        };
    });

    // 2. Extraire la liste unique de toutes les compagnies et de tous les clients
    const allCompagnies = Array.from(
        new Set(periodData.flatMap(p => Object.keys(p.compagnies || {})))
    ).sort();

    const allClients = Array.from(
        new Set(periodData.flatMap(p => Object.keys(p.clients || {})))
    ).sort();

    return {
        periods,
        periodData,
        allCompagnies,
        allClients,
        activityFilter: filter
    };
};

/**
 * Formatage d'un montant monétaire
 */
export const formatCurrency = (val, isEmpty = false) => {
    if (isEmpty || val === null || val === undefined) return '';
    return new Intl.NumberFormat('fr-FR', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    }).format(Math.round(val)).replace(/[^\d-]/g, ' ');
};

/**
 * Formatage d'un pourcentage
 */
export const formatPercentage = (val, isEmpty = false) => {
    if (isEmpty || val === null || val === undefined || isNaN(val)) return '';
    return `${Number(val).toFixed(2)} %`;
};
